import { useAtom } from "jotai";
import { Grid, Select, useMantineColorScheme } from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { componentsAtom } from "../../../utils/atoms/componentsAtom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import batteryJson from "../../../components/help/helpCardMenu.json";
import { helpAtom } from "../../../utils/atoms/helpAtom";
import { mgaRegex, responsiveCol } from "../../../constants/helper";
import { helpContextMenuAtom } from "../../../utils/atoms/helpContextMenuAtom";
import ProjectNumberInput from "../../common/ProjectNumberInput";
import { useTranslation } from "react-i18next";

const EMPTY_HELP_SECTION = { title: "", description: "", type: "", other: "" };

const highlightCard = (dataId) => {
  const card = document.querySelector(`[card-id="${dataId}"]`);
  if (!card) return;
  card.scrollIntoView({ behavior: "smooth", block: "center" });
  card.style.transition = "background-color 0.5s ease";
  card.style.border = "2px solid #42b4e6";
  setTimeout(() => { card.style.border = ""; }, 2000);
};

const BatteryControl = ({ handleInputChange, form }) => {
  const { t } = useTranslation();
  const [projectComponents] = useAtom(componentsAtom);
  const [batteryParameters, setBatteryParameters] = useState({});
  const containerRef = useRef(null);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const { colorScheme } = useMantineColorScheme();
  const [helpMenuData] = useAtom(helpContextMenuAtom);

  useEffect(() => {
    setBatteryParameters(projectComponents?.battery);
  }, [projectComponents]);

  const graySection = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme]
  );

  const handleBlur = useCallback(
    (event) => {
      if (!containerRef.current?.contains(event.relatedTarget)) {
        setHelperAtom({ ...helperAtom, isFocused: false, helpSection: EMPTY_HELP_SECTION });
      }
    },
    [helperAtom, setHelperAtom]
  );

  const handleFocus = useCallback((event) => {
    const dataId = event.target.getAttribute("data-id");
    if (dataId) highlightCard(dataId);
  }, []);

  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.battery?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  }

  const batteryParams = batteryParameters?.uiAttributes?.batteryParameters;
  const chargingOptions = [
    { label: t("battery.control.chargingSource.option.pv"), value: "PV" },
    {
      label: t("battery.control.chargingSource.option.noConstraint"),
      value: "No Constraint",
    },
  ];

  return (
    <div ref={containerRef} onFocus={handleFocus} onBlur={handleBlur}>
      <Grid gutter="md" align="flex-start" className="mt-2 mb-2">
        <Grid.Col span={8} sm={8} md={7} lg={4}>
          <ProjectNumberInput
            {...getFieldAttributes("field-17")}
            data-id="field-17"
            label={t("battery.control.annualCycles.label")}
            placeholder={t("battery.control.annualCycles.placeholder")}
            value={batteryParams?.annualChargeCycle}
            {...form.getInputProps("uiAttributes.batteryParameters.annualChargeCycle")}
            onChange={(e) => {
              const value = e.currentTarget.value;
              if (!mgaRegex.integerInputRegex.test(value)) return;
              handleInputChange(
                "uiAttributes",
                "annualChargeCycle",
                value === "" ? "" : Number(value)
              );
            }}
            rightSection={<div style={graySection}>{t("battery.control.numberUnit")}</div>}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col span={12} sm={12} md={5} lg={4}>
          <Select
            {...getFieldAttributes("field-18")}
            data-id="field-18"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("battery.control.chargingSource.label")}
            placeholder={t("battery.control.chargingSource.placeholder")}
            data={chargingOptions}
            value={batteryParams?.chargingMethod || "No Constraint"}
            {...form.getInputProps("uiAttributes.batteryParameters.chargingMethod")}
            onChange={(value) => handleInputChange("uiAttributes", "chargingMethod", value)}
            withinPortal
          />
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default BatteryControl;
